<script lang="ts">
  import { currencyPHP } from '@epitome-analytics/dashboard-kit';
  import { store, type TaggedRow, type TagSignal } from '../lib/stores.svelte.js';
  import { fmtShare } from '../lib/format.js';

  const grouped = $derived(() => {
    const out = new Map<TagSignal, TaggedRow[]>();
    for (const r of store.tagged.values()) {
      const arr = out.get(r.signal) ?? [];
      arr.push(r);
      out.set(r.signal, arr);
    }
    return out;
  });

  const totals = $derived(() => {
    let sop = 0, myGmv = 0, catGmv = 0;
    for (const r of store.tagged.values()) {
      sop    += r.sop;
      myGmv  += r.my_gmv;
      catGmv += r.cat_gmv;
    }
    const avgShare = catGmv > 0 ? myGmv / catGmv : 0;
    return { sop, myGmv, catGmv, avgShare };
  });

  function exportCSV() {
    const header = [
      'City','Province','Brand','SKU','Signal','GPIv',
      'Size of Prize (PHP)','Cat GMV (PHP)','My GMV (PHP)','My Share%','Mkt Potential','Captured At',
    ].join(',');
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [...store.tagged.values()].map((r) =>
      [esc(r.city), esc(r.province), esc(r.brand), esc(r.sku), esc(r.signal),
       r.gpiv.toFixed(4),
       Math.round(r.sop), Math.round(r.cat_gmv), Math.round(r.my_gmv),
       (r.share * 100).toFixed(1), r.mp, esc(r.captured_at)].join(',')
    );
    const csv  = header + '\n' + lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `tagged_plan_${store.period || 'period'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function printPlan() {
    window.print();
  }
</script>

{#if store.taggedOpen}
  <div class="overlay" onclick={(e) => { if (e.target === e.currentTarget) store.taggedOpen = false; }} role="presentation">
    <div class="panel">
      <header>
        <span class="eyebrow">Tagged Plan · {store.period}</span>
        <h2>Allocation Strategy</h2>
        <div class="actions">
          <button onclick={exportCSV}>Export CSV</button>
          <button onclick={printPlan}>Print</button>
          <button onclick={() => store.clearTags()} class="ghost">Clear all</button>
          <button onclick={() => (store.taggedOpen = false)} class="close">×</button>
        </div>
      </header>

      <div class="totals">
        <div><span>Tagged rows</span><strong>{store.tagged.size}</strong></div>
        <div><span>Total SoP</span><strong>{currencyPHP.formatPHPShort(totals().sop)}</strong></div>
        <div><span>My GMV</span><strong>{currencyPHP.formatPHPShort(totals().myGmv)}</strong></div>
        <div><span>Avg share</span><strong>{fmtShare(totals().avgShare)}</strong></div>
      </div>

      {#if store.tagged.size === 0}
        <div class="empty">No tagged rows yet. Star a row in the rankings table to add it here.</div>
      {:else}
        {#each [...grouped()] as [sig, rows] (sig)}
          <section>
            <h3>{sig} <span class="cnt">{rows.length}</span></h3>
            <table>
              <thead>
                <tr>
                  <th>City</th><th>Province</th><th>Brand</th><th>SKU</th>
                  <th class="num">GPIv</th><th class="num">SoP</th>
                  <th class="num">Cat GMV</th><th class="num">My Share</th><th class="num">MP</th>
                </tr>
              </thead>
              <tbody>
                {#each rows as r (`${r.city_id}|${r.sku_id}`)}
                  <tr>
                    <td>{r.city}</td>
                    <td>{r.province}</td>
                    <td>{r.brand}</td>
                    <td>{r.sku}</td>
                    <td class="num">{r.gpiv.toFixed(2)}</td>
                    <td class="num">{currencyPHP.formatPHPShort(r.sop)}</td>
                    <td class="num">{currencyPHP.formatPHPShort(r.cat_gmv)}</td>
                    <td class="num">{fmtShare(r.share)}</td>
                    <td class="num">{r.mp}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </section>
        {/each}
      {/if}
    </div>
  </div>
{/if}

<style>
  .overlay {
    position: fixed; inset: 0;
    background: rgba(13, 46, 74, 0.45);
    z-index: var(--z-modal);
    display: flex; align-items: center; justify-content: center;
    padding: var(--space-xl);
  }
  .panel {
    background: var(--color-white);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-modal);
    width: min(1100px, 100%);
    max-height: 90vh;
    overflow-y: auto;
    padding: var(--space-xl);
    font-family: var(--font-ui);
  }
  header {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: end;
    gap: var(--space-md);
    margin-bottom: var(--space-lg);
  }
  .eyebrow {
    font-size: var(--text-xs);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-wider);
    text-transform: uppercase;
    color: var(--color-blue);
  }
  h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    text-transform: uppercase;
    color: var(--color-navy);
  }
  .actions { display: flex; gap: var(--space-sm); align-items: center; }
  .actions button {
    font-family: var(--font-ui);
    font-size: var(--text-xs);
    font-weight: var(--weight-semibold);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    padding: 6px 12px;
    border-radius: var(--radius-sm);
    background: var(--color-blue);
    color: var(--color-white);
    border: none;
    cursor: pointer;
  }
  .actions .ghost { background: transparent; color: var(--color-text-muted); border: 1px solid var(--color-border); }
  .actions .close { background: transparent; color: var(--color-text-muted); border: none; font-size: var(--text-xl); padding: 4px 8px; }
  .totals {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--space-md);
    background: var(--color-surface);
    border-radius: var(--radius-md);
    padding: var(--space-md);
    margin-bottom: var(--space-lg);
  }
  .totals span { display: block; font-size: var(--text-xs); color: var(--color-text-muted); text-transform: uppercase; letter-spacing: var(--tracking-wide); }
  .totals strong { font-family: var(--font-display); font-size: var(--text-xl); color: var(--color-navy); }
  section { margin-bottom: var(--space-lg); }
  section h3 {
    margin: 0 0 var(--space-sm);
    font-family: var(--font-display);
    text-transform: uppercase;
    color: var(--color-navy);
  }
  .cnt { color: var(--color-text-muted); font-weight: var(--weight-regular); margin-left: var(--space-sm); }
  table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
  th, td { padding: 8px 10px; border-bottom: 1px solid var(--color-border); }
  th { background: var(--color-surface); font-size: var(--text-xs); color: var(--color-text-muted); text-transform: uppercase; letter-spacing: var(--tracking-wide); text-align: left; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .empty { padding: var(--space-2xl); text-align: center; color: var(--color-text-muted); }
  @media print {
    .overlay { position: static; background: white; padding: 0; }
    .actions { display: none; }
    .panel { box-shadow: none; max-height: none; }
  }
</style>
